import React, { useCallback, useEffect, useState } from 'react';
import { formatSparks, getSparksHistory, getSparksState, setLeaderboardOptOut } from '../../api/sparksApi';

const EVENT_LABELS = {
  'question.correct': 'Correct answer',
  'question.learned_it': 'Learned it (follow-up)',
  'question.streak_milestone': 'In a row bonus',
  'lesson.step_correct': 'Lesson question',
  'lesson.finished': 'Lesson finished',
  'lesson.level_up': 'Topic levelled up',
  'quiz.completed': 'Quiz completed',
  'homework.completed': 'Homework completed',
  'worksheet.completed': 'Worksheet completed',
  'coreskill.mastered_prequiz': 'Core Skill mastered (pre-quiz)',
  'coreskill.completed': 'Core Skill completed',
  'improvement.new_best': 'New best score',
  'streak.day': 'Daily streak',
  'streak.milestone': 'Streak milestone',
  'achievement.first': 'Badge',
  'achievement.milestone': 'Milestone badge',
  'tutor.shoutout': 'Tutor shout-out',
  'admin.adjust': 'Adjustment by Nova',
  'shop.purchase': "Bought in Addy's Shop",
  'shop.refund': 'Refund',
};

/** Parent view on the Progress Dashboard: balance, lifetime, badges, history, leaderboard opt-out (§9.1, §9.3). */
export default function SparksParentPanel({ childId }) {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!childId) return;
    try {
      const [s, h] = await Promise.all([getSparksState(childId), getSparksHistory(childId, 100)]);
      setState(s);
      setHistory(h);
    } catch {
      setError('Could not load Sparks right now.');
    }
  }, [childId]);

  useEffect(() => { load(); }, [load]);

  const toggleOptOut = async () => {
    if (!state) return;
    setSaving(true);
    try {
      await setLeaderboardOptOut(childId, !state.leaderboardOptOut);
      setState((s) => ({ ...s, leaderboardOptOut: !s.leaderboardOptOut }));
    } catch {
      setError('Could not save that setting.');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="text-red-700 text-sm">{error}</div>;
  if (!state) return <div className="text-gray-600 text-sm">Loading Sparks…</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div className="rounded-xl border border-[#E6DDFB] bg-white p-3">
          <div className="text-xs text-gray-500">Sparks to spend</div>
          <div className="text-2xl font-bold text-[#2F1E6B]">{formatSparks(state.balance)} ✨</div>
        </div>
        <div className="rounded-xl border border-[#E6DDFB] bg-white p-3">
          <div className="text-xs text-gray-500">Sparks earned all-time</div>
          <div className="text-2xl font-bold text-[#2F1E6B]">{formatSparks(state.lifetime)}</div>
        </div>
      </div>

      <div className="text-sm text-[#2F1E6B]">
        Level {state.level} · {state.streakBadges.filter((b) => b.earnedAt).length} of {state.streakBadges.length} streak badges · {state.dailyStreak}-day streak
        {state.streakFreezes > 0 && ` · ${state.streakFreezes} streak ${state.streakFreezes === 1 ? 'freeze' : 'freezes'}`}
      </div>

      <div className="rounded-xl border border-[#E6DDFB] bg-white p-3 max-w-md flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-[#2F1E6B]">Show on the weekly leaderboard</div>
          <div className="text-xs text-gray-500">First name and Addy only. Never surname, year group or school.</div>
        </div>
        <button onClick={toggleOptOut} disabled={saving} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${state.leaderboardOptOut ? 'bg-gray-200 text-gray-700' : 'bg-[#A1CC3A] text-[#2F1E6B]'}`}>
          {state.leaderboardOptOut ? 'Hidden' : 'Shown'}
        </button>
      </div>

      {state.achievements?.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-[#2F1E6B] mb-1">Badges</div>
          <div className="flex flex-wrap gap-2">
            {state.achievements.map((a) => (
              <span key={a.key} className="text-xs bg-[#FFF8DC] border border-[#FFD966] rounded-full px-2 py-1" title={new Date(a.earnedAt).toLocaleDateString('en-GB')}>🏅 {a.label}</span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-sm font-semibold text-[#2F1E6B] mb-1">Recent Sparks</div>
        <div className="rounded-xl border border-[#E6DDFB] bg-white divide-y divide-[#F1EEF7] max-h-96 overflow-auto">
          {history.length === 0 && <div className="p-3 text-sm text-gray-500">No Sparks yet — they arrive with the first correct answer.</div>}
          {history.map((row, i) => (
            <div key={`${row.createdAt}-${i}`} className="flex items-center justify-between px-3 py-2 text-sm">
              <div>
                <div className="text-[#2F1E6B]">{EVENT_LABELS[row.event] || row.event}</div>
                <div className="text-xs text-gray-500">{new Date(row.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div className={`font-semibold ${row.amount < 0 ? 'text-gray-600' : 'text-green-700'}`}>{row.amount < 0 ? '−' : '+'}{formatSparks(Math.abs(row.amount))}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
